// The player pool (PLAYERS.md). Drawn through the kit (LOOKS.md Part 7).
//
// ONE TABLE (B-518, B-520, PLAN-2026-10-26-numbers-that-fit-the-position
// §9.1): every row starts with the same fantasy columns — points, points a
// game, his projection and this week's opponent, then from Standard his
// position rank and last three, and at Everything his lowest and highest
// week and the site's rostered share — and the position chip decides what
// follows: that position's own stat sheet a game (B-517), or with every
// position nothing more, since no position's columns fit every row. Until
// 2026-10-26 Players had two views, Fantasy and Stats, and with every
// position the Stats view split each player's points by kind of play.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  COMPARE_MAX,
  failureOf,
  valueOf,
  PlayersQuery,
  type PlayerRowView,
  type PlayerSort,
  type PositionFilterView,
  type StatRange,
} from '@fsp/domain';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLeagueBase, useLeagueZoneState, usePlayers, useReaderZone } from '../data/hooks';
import { useLeagueSource } from '../data/leagueSource';
import { useAtLeast, useDetail } from '../data/detail';
import { useTools } from '../data/tools';
import { Button, Chips, Choice, Line, Stack, TextField } from '../kit/ui';
import { useSettled } from '../kit/viewport';
import '../styles/screens/players.css';
import { ListScreen } from '../templates/ListScreen';
import { useTier } from '../shell/useTier';
import { useRemembered } from '../shell/tabMemory';
import { leagueIdFromPath } from '../data/route';
import { num, pct } from './shared/notComputed';
import { ProjectionWhy, projectionNote } from './shared/Projection';
import { Term } from '../kit/Why';
import { GLOSSARY } from './shared/glossary';
import { PlayerCard, type PlayerCardData } from './shared/PlayerCard';
import { WatchStar } from './shared/WatchStar';
import { rangeWords } from './shared/Deeper';
import { useWatchlist } from '../data/watchlist';
import { StaleNotice } from './shared/FromLeague';
import { fmtWhen, RosterMoveSheet, type RosterMove } from './shared/RosterMoveSheet';
import { MatchupChip } from './shared/MatchupChip';
import { rankText, seasonRankWords } from './shared/rank';
import { depthTag, depthTip, usePositionNames, useProLeague } from './shared/depth';
import { useCompareTray } from '../shell/compareTray';
import { Icon } from '../kit/Icon';
import { fallbackChoices, samePositions } from './shared/positions';
import { DataCredit } from './shared/DataCredit';
import {
  STAT_RANGES,
  StatsTable,
  columnsAt,
  rangeLabel,
  rangePointsTip,
  type LeadColumn,
  type StatsTableRow,
} from './shared/StatsTable';
import { clubLabel } from '../sports/registry';

/**
 * A row's tick while picking players to compare (B-375): a checkbox a
 * finger's 44 tall, its name the player's. A full tray takes nobody new,
 * and says so on the box.
 */
function PickBox({
  name,
  on,
  full,
  onToggle,
}: {
  name: string;
  on: boolean;
  full: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on}
      aria-label={`Compare ${name}`}
      className="fsp-pick"
      disabled={!on && full}
      onClick={onToggle}
    >
      <span className="fsp-pick-box" aria-hidden>
        {on ? <Icon name="accept" size="sm" /> : null}
      </span>
    </button>
  );
}

/** A figure with its meaning when asked, or the figure alone when there is none to give. */
function Explained({ tip, children }: { tip: string | null; children: ReactNode }) {
  return tip === null ? <>{children}</> : <Term tip={tip}>{children}</Term>;
}

/** The list's order (B-366): a figure, or one stat column (`sort=stat`); highest first unless turned round. */
interface Order {
  sort: PlayerSort | null;
  stat: string | null;
  reverse: boolean;
}

/** The pool's own order: points a game (B-365). */
const DEFAULT_ORDER: Order = { sort: null, stat: null, reverse: false };

/** A stat column's order in the menu, beside the figures' (`sort=stat`). */
const STAT = 'stat:';

/**
 * This week's opponent and, from Standard, how it has done against his
 * position (B-249): "vs DAL", "at SF". No kickoff and no score (B-520):
 * Game Day has the week's games, and a kickoff's length set the column's
 * width for every row.
 */
function Opponent({ row }: { row: PlayerRowView }) {
  const g = row.game;
  if (g?.state === 'bye') return <span className="fsp-muted">Bye</span>;
  if (g === null || !g.opponent) return <span className="fsp-muted">—</span>;
  return (
    <span className="fsp-stats-game-in">
      <span>{`${g.home ? 'vs' : 'at'} ${clubLabel(g.opponent)}`}</span>
      <MatchupChip cue={row.matchup} position={row.player.positions[0]} opponent={g.opponent} />
    </span>
  );
}

/** The points column's name in the order menu, under the range. */
function pointsWords(range: StatRange, real: boolean): string {
  if (!real) return 'Season points';
  switch (range) {
    case 'season':
      return 'Season points';
    case 'recent':
      return 'Points over the last three games';
    case 'week':
      return 'Points last week';
    case 'projected':
      return 'Projected points';
  }
}

/** What points a game holds under the range, said when asked. */
function averageTip(range: StatRange, real: boolean): string {
  if (!real || range === 'season') return GLOSSARY.seasonAverage;
  switch (range) {
    case 'recent':
      return GLOSSARY.lastThree;
    case 'week':
      return 'His points last week, in this league’s scoring.';
    case 'projected':
      return 'His projected points this week, in this league’s scoring.';
  }
}

export function PlayersScreen() {
  const leagueKey = leagueIdFromPath(useLocation().pathname) ?? 'none';
  // KEPT BY THE TAB (UX-39): Players and back finds the same search.
  const [availability, setAvailability] = useRemembered<PlayersQuery['availability']>(
    `players:${leagueKey}:availability`,
    'free-agents',
  );
  const [positions, setPositions] = useRemembered<string[]>(`players:${leagueKey}:positions`, []);
  // ONE PRO CLUB (E1, 2026-10-22): its players, in the order of its depth
  // chart when the Depth column is pressed. Kept by the tab with the rest of
  // the search.
  const [club, setClub] = useRemembered<string | null>(`players:${leagueKey}:club`, null);
  const proLeague = useProLeague();
  const positionNames = usePositionNames();
  const [q, setQ] = useRemembered(`players:${leagueKey}:q`, '');
  const source = useLeagueSource();
  const real = source === 'api';
  const watchlist = useWatchlist({ fresh: real });
  // ONE ORDER (B-518), kept by the tab: a figure's or a stat column's, a
  // head's press sorting by it and a second press turning it round (B-366).
  const [order, setOrder] = useRemembered<Order>(`players:${leagueKey}:order`, DEFAULT_ORDER);
  // WHICH GAMES the points and the stats are over (B-412): the season, the
  // last three, last week or this week's projection. A real league's alone:
  // the tour's weeks are made up and have no stat lines behind them.
  const [rangeKept, setRange] = useRemembered<StatRange>(`players:${leagueKey}:range`, 'season');
  // A HELPER THE LEAGUE TURNED OFF (RULES_SCHEMA §15c) is no column, order or
  // range here: its numbers are not served, and asking for them is refused.
  // A range or an order kept from before it was turned off is the season's.
  const tools = useTools();
  const range: StatRange = rangeKept === 'projected' && !tools.projections ? 'season' : rangeKept;
  // THE POSITIONS THE LEAGUE'S LINEUP OFFERS (B-511), one at a time: each
  // position it starts and each slot that takes several (Flex), from the last
  // page, kept while the next one reads. Before the first, the positions alone.
  const [heldChoices, setHeldChoices] = useState<readonly PositionFilterView[]>([]);
  const choices: readonly PositionFilterView[] =
    heldChoices.length > 0 ? heldChoices : fallbackChoices();
  const chosen =
    positions.length === 0
      ? null
      : (choices.find((f) => samePositions(f.positions, positions)) ?? null);
  // THE STAT COLUMNS LAST DRAWN, kept while the next page reads, so the
  // order a stat's column has left is set aside rather than forgotten (the
  // review of B-412): it comes back with its column.
  const [drawnStats, setDrawnStats] = useState<string | null>(null);
  const phone = useTier() === 'phone';
  const level = useDetail();
  const standard = useAtLeast('standard');
  const pro = useAtLeast('pro');
  // AN ORDER BY A COLUMN THAT IS NOT DRAWN IS NOT THE ORDER (B-366's rule):
  // a column a lower level leaves out, or a stat of another position's sheet,
  // leaves the list in the pool's own order until its column is back.
  const drawn = (o: Order): boolean => {
    switch (o.sort) {
      case 'stat':
        return o.stat !== null && (drawnStats ?? '').split(',').includes(o.stat);
      // Its column goes under "Last 3 games", where the range is the last three.
      case 'recent':
        return standard && !(real && range === 'recent');
      case 'projection':
        return tools.projections;
      case 'trend':
        return pro && tools.suggestions;
      case 'low':
      case 'range':
      case 'ownership':
        return pro;
      // Its column went with the list's usage columns (B-517): Tgt% is a
      // stat. Form's went too (B-520): its line said little a list needs.
      case 'usage':
      case 'form':
        return false;
      default:
        return true;
    }
  };
  const applied = drawn(order) ? order : DEFAULT_ORDER;
  // A real league asks the server as you type, so it waits for a pause
  // (PLAYERS.md: debounced 150 ms); the simulator answers in the same frame.
  const settledQ = useSettled(q, 150);
  const [card, setCard] = useState<PlayerCardData | null>(null);
  const [move, setMove] = useState<RosterMove | null>(null);
  const navigate = useNavigate();
  const base = useLeagueBase();
  // PICKING PLAYERS TO COMPARE (B-375): a mode, kept by the tab, in which the
  // action column holds a tick for each row and the tray at the foot of the
  // screen holds who is ticked.
  const tray = useCompareTray();
  const [picking, setPicking] = useRemembered<boolean>(`players:${leagueKey}:picking`, false);
  const pickToggle = picking ? null : (
    <Button size="xs" variant="secondary" icon="compare" onClick={() => setPicking(true)}>
      Compare players
    </Button>
  );
  // Times in the reader's zone (B-181); the league's read only matters to an
  // account with none, which waits on it.
  const clock = useLeagueZoneState();
  const zone = useReaderZone();

  // Two hundred in one page for the simulator, which computes them all anyway;
  // a real league reads a hundred at a time and the next as the list nears
  // its end (W8, B-369).
  const query = useMemo(
    () =>
      PlayersQuery.parse({
        availability,
        positions,
        ...(club ? { club } : {}),
        q: real ? settledQ : q,
        // EVERY ROW'S NUMBERS UNDER THE RANGE (B-518): its points, points a
        // game and stats, and the points and average orders by them.
        ...(real ? { stats: range } : {}),
        ...(applied.sort === null ? {} : { sort: applied.sort }),
        ...(applied.sort === 'stat' && applied.stat !== null ? { stat: applied.stat } : {}),
        reverse: applied.reverse,
        limit: real ? 100 : 200,
      }),
    [
      availability,
      positions,
      club,
      q,
      settledQ,
      real,
      range,
      applied.sort,
      applied.stat,
      applied.reverse,
    ],
  );
  const pool = usePlayers(query);
  const page = valueOf(pool.snapshot);
  const items = page?.items ?? [];
  // THE CLUBS THE SERVER NAMES (B-479), kept while the next page reads.
  const heldClubs = useRef<{ id: string; name: string }[]>([]);
  if (page?.clubs.length) heldClubs.current = page.clubs;
  const clubs = heldClubs.current;
  const offeredKey = JSON.stringify(page?.filters ?? []);
  useEffect(() => {
    const offered = JSON.parse(offeredKey) as PositionFilterView[];
    if (offered.length > 0) setHeldChoices(offered);
  }, [offeredKey]);
  // A CHOICE THE LINEUP NO LONGER OFFERS — several positions picked before
  // the chips were one at a time — is every position, rather than a list
  // narrowed by chips none of which is lit.
  useEffect(() => {
    const offered = page?.filters ?? [];
    if (
      offered.length > 0 &&
      positions.length > 0 &&
      !offered.some((f) => samePositions(f.positions, positions))
    )
      setPositions([]);
  }, [page, positions, setPositions]);
  // THE POSITION'S SHEET (B-517) at the reader's level: Simple's few, then
  // Standard's, every one at Everything. With every position, none.
  const statGroup = page?.statGroups[0] ?? null;
  const statColumns = statGroup === null ? [] : columnsAt(statGroup.columns, level);
  const statKeys = statColumns.map((c) => c.key).join(',');
  useEffect(() => {
    if (page !== null) setDrawnStats(statKeys);
  }, [page, statKeys]);
  // A COLUMN NOBODY CAN FILL IS NOT DRAWN (plan rule 8). Ownership and trend
  // come from the site's own leagues that are drafting or playing (B-251);
  // the deeper numbers from a real league's season, which the tour has not.
  const showOwn = items.some((r) => r.ownershipPct !== null || r.trend !== null);
  const hasDeeper = items.some((r) => r.deeper !== null);
  // THE DEPTH CHART'S COLUMN (B-479) where somebody listed is on one: a world
  // with no chart (the simulator's), or a page of nobody on one (the
  // defenses), has none.
  const chartKnown = items.some((r) => r.player.depth);
  const sort: PlayerSort = applied.sort ?? page?.sort ?? 'average';
  const failure = failureOf(pool.snapshot);

  /** A figure's order: a new one starts highest first, the one the list is in turns round (B-366). */
  const sortBy = (next: PlayerSort, stat: string | null = null) => {
    const on = sort === next && (next !== 'stat' || applied.stat === stat);
    setOrder(on ? { ...applied, reverse: !applied.reverse } : { sort: next, stat, reverse: false });
  };
  const headSort = (by: PlayerSort, first: 'high' | 'low' = 'high') => ({
    on: sort === by,
    reverse: applied.reverse,
    first,
    onSort: () => sortBy(by),
  });

  // Add, Claim, and Drop open the roster-move sheet (WAIVERS.md); trades arrive with TRADES.md.
  const act = (r: PlayerRowView) => {
    if (r.action === 'none') return;
    if (r.action === 'trade') {
      // The builder with his owner and him filled in (TRADES.md "Trade for").
      if (r.availability.kind === 'owned')
        navigate(
          `${base}/trades?new=1&with=${r.availability.team.id}&get=${encodeURIComponent(r.player.id)}`,
        );
      return;
    }
    setCard(null);
    setMove({
      kind: r.action,
      playerId: r.player.id,
      claimId: r.availability.kind === 'waivers' ? r.availability.myClaimId : null,
    });
  };

  /** A row's own control: the tick while picking (B-375), else what can be done with him. */
  const actionOf = (r: PlayerRowView): ReactNode =>
    picking ? (
      <PickBox
        name={r.player.name}
        on={tray.has(r.player.id)}
        full={tray.full}
        onToggle={() => tray.toggle({ id: r.player.id, name: r.player.name })}
      />
    ) : r.action !== 'none' ? (
      <Button
        size="xs"
        full
        variant={
          r.availability.kind === 'waivers' && r.availability.myClaimId
            ? 'secondary'
            : r.action === 'claim'
              ? 'caution-soft'
              : r.action === 'drop'
                ? 'danger-quiet'
                : // A ROW'S ADD IS THE LIST'S, NOT THE SCREEN'S (B-278):
                  // tinted rather than filled, so a page of players is
                  // not a page of equally loud buttons (UX-15).
                  'soft'
        }
        onClick={() => act(r)}
        // A PHONE'S SHORTER WORD (B-518): its fixed column at the right
        // edge takes the numbers' room; the card says "Trade for".
        label={phone && r.action === 'trade' ? `Trade for ${r.player.name}` : undefined}
      >
        {phone && r.action === 'trade' ? 'Trade' : actionLabel(r)}
      </Button>
    ) : null;

  const openCard = (r: PlayerRowView) =>
    setCard({
      player: r.player,
      game: r.game,
      projection: r.projection,
      basis: r.basis,
      points: null,
      statLine: null,
      action: r.action === 'none' ? null : { label: actionLabel(r), onClick: () => act(r) },
    });

  /** Under the name: who holds him or when he clears, and why nothing can be done. */
  const subOf = (r: PlayerRowView): ReactNode => {
    const held =
      r.availability.kind === 'waivers'
        ? // No time until the reader's zone is known: still on waivers.
          zone !== null
          ? `On waivers until ${fmtWhen(r.availability.clearsAt, zone)}`
          : 'On waivers'
        : r.availability.kind === 'owned'
          ? r.availability.team.ownerName
          : r.locked && r.action === 'none' && r.reason === null
            ? 'Locked'
            : null;
    // WHY THIS ROW OFFERS NOTHING, in the server's words (B-147, B-152), at
    // every detail level — a sentence, not a number.
    const lines = [
      held,
      r.action === 'none' ? r.reason : null,
      projectionNote(r.projection, r.basis),
    ].filter((x): x is string => x !== null);
    return lines.length === 0 ? null : lines.join(' · ');
  };

  // THE FANTASY COLUMNS (B-518), the same before every position's stats.
  const pointsOf = (r: PlayerRowView) => (r.stats !== null ? r.stats.points : r.seasonPoints);
  const averageOf = (r: PlayerRowView) => (r.stats !== null ? r.stats.average : r.seasonAvg);
  const several = chosen === null || chosen.positions.length > 1;
  type Row = StatsTableRow & { row: PlayerRowView };
  const leads: LeadColumn<Row>[] = [
    // THE WEEK'S FIGURES FIRST (B-604): points a game, the last three beside
    // it, this week's projection and the game it bears on, so a phone's first
    // screen carries what a pickup is weighed by; then the season's total and
    // the rank it makes.
    {
      key: 'average',
      label: 'Avg',
      tip: averageTip(range, real),
      sort: headSort('average'),
      strong: sort === 'average',
      cell: ({ row }) => num(averageOf(row)),
    },
    // The last three are the range itself under "Last 3 games".
    ...(standard && !(real && range === 'recent')
      ? [
          {
            key: 'recent',
            label: 'L3',
            tip: GLOSSARY.lastThree,
            sort: headSort('recent'),
            strong: sort === 'recent',
            cell: ({ row }: Row) => num(row.lastThreeAvg),
          },
        ]
      : []),
    // No column for a helper the league turned off (§15c).
    ...(tools.projections
      ? [
          {
            key: 'projection',
            label: 'Proj',
            tip: GLOSSARY.weekProjection,
            sort: headSort('projection'),
            strong: sort === 'projection',
            // A week on a bye says so where its projection would be (B-365).
            cell: ({ row }: Row) =>
              row.game?.state === 'bye' ? (
                <span className="fsp-muted fsp-players-bye">BYE</span>
              ) : (
                // The projection says what it rests on here too, as on My Team.
                <ProjectionWhy player={row.player} projection={row.projection} basis={row.basis}>
                  {num(row.projection)}
                </ProjectionWhy>
              ),
          },
        ]
      : []),
    {
      key: 'opp',
      label: 'Opp',
      tip: GLOSSARY.opponent,
      words: true,
      cell: ({ row }) => <Opponent row={row} />,
    },
    {
      key: 'points',
      label: 'Pts',
      tip: real ? rangePointsTip(range, page?.statWeek ?? null) : GLOSSARY.seasonPoints,
      sort: headSort('points'),
      strong: sort === 'points',
      cell: ({ row }) => num(pointsOf(row)),
    },
    // HIS POSITION'S RANK where the list spans several (B-321): "RB4".
    ...(standard && several
      ? [
          {
            key: 'pos',
            label: 'Pos',
            tip: 'His rank at his position by season points: RB4 has the fourth-most points of any running back.',
            cell: ({ row }: Row) =>
              row.seasonRank === null ? (
                '—'
              ) : (
                <Term tip={seasonRankWords(row.player.positions[0]!, row.seasonRank)}>
                  {rankText(row.player.positions[0]!, row.seasonRank)}
                </Term>
              ),
          },
        ]
      : []),
    // HIS LOWEST AND HIGHEST WEEK, a column each (B-520): a floor and a
    // ceiling read apart, and each sorts.
    ...(pro && hasDeeper
      ? [
          {
            key: 'low',
            label: 'Low',
            tip: 'His lowest week this season.',
            sort: headSort('low'),
            strong: sort === 'low',
            cell: ({ row }: Row) => (
              <Explained tip={row.deeper?.range ? rangeWords(row.deeper.range) : null}>
                {num(row.deeper?.range?.low ?? null)}
              </Explained>
            ),
          },
          {
            key: 'range',
            label: 'High',
            tip: 'His highest week this season.',
            sort: headSort('range'),
            strong: sort === 'range',
            cell: ({ row }: Row) => (
              <Explained tip={row.deeper?.range ? rangeWords(row.deeper.range) : null}>
                {num(row.deeper?.range?.high ?? null)}
              </Explained>
            ),
          },
        ]
      : []),
    ...(pro && showOwn
      ? [
          {
            key: 'ownership',
            label: 'Own',
            tip: GLOSSARY.owned,
            sort: headSort('ownership'),
            strong: sort === 'ownership',
            cell: ({ row }: Row) => (
              <>
                {pct(row.ownershipPct)}
                {/* No arrow for a trend nobody measured: a flat one would say "steady". */}
                {row.trend === null ? null : (
                  <>
                    {' '}
                    <span className="fsp-players-trend" data-trend={row.trend}>
                      {row.trend === 'up' ? '▲' : row.trend === 'down' ? '▼' : '–'}
                    </span>
                  </>
                )}
              </>
            ),
          },
        ]
      : []),
    // HIS DEPTH CHART PLACE (B-479) last of them, away from his rank's
    // "RB1", which reads the same and means another thing.
    ...(chartKnown
      ? [
          {
            key: 'depth',
            label: 'Depth',
            tip: 'His place on his club’s depth chart: QB1 is its first quarterback, RB2 its second running back. Sorts by position A to Z, each from its first string down.',
            sort: headSort('depth', 'low'),
            strong: sort === 'depth',
            cell: ({ row }: Row) => (
              <Explained
                tip={
                  row.player.depth
                    ? depthTip(
                        row.player.depth,
                        row.player.proTeam,
                        positionNames,
                        row.player.positions[0],
                      )
                    : row.player.proTeam === null
                      ? null
                      : `Not on ${clubLabel(row.player.proTeam)}’s depth chart.`
                }
              >
                {row.player.depth ? depthTag(row.player.depth) : '—'}
              </Explained>
            ),
          },
        ]
      : []),
  ];

  const failureLine =
    zone === null && clock.failure !== null ? (
      // The rows lose their clear times without the league's zone; say so.
      <Line spread gap={2}>
        <span className="fsp-sub fsp-ui-wrap-text">Clear times are not shown: {clock.failure}</span>
        <Button size="xs" onClick={clock.retry}>
          Try again
        </Button>
      </Line>
    ) : null;
  // WHY THERE IS NO PROJECTION, where a manager looks for one, at Standard
  // and up: Simple adds no words (RULES_SCHEMA §15c, DETAIL_LEVELS).
  const toolsLine =
    !tools.projections && standard ? (
      <span className="fsp-sub fsp-muted">Projections are off in this league.</span>
    ) : null;
  // WHICH GAMES (B-412): a real league's points and stats over them.
  const rangeLine = real ? (
    <span className="fsp-players-range">
      <Choice
        label="Which games"
        hideLabel
        searchable={false}
        required
        value={range}
        onChange={(v) => {
          if (v) setRange(v as StatRange);
        }}
        options={STAT_RANGES.filter((r) => tools.projections || r.value !== 'projected').map(
          (r) => ({
            value: r.value,
            label: rangeLabel(r, page?.statWeek ?? null),
          }),
        )}
      />
    </span>
  ) : null;
  // THE ORDER IN WHOLE WORDS (B-366): the column heads are short, and this
  // list says what each order is. Only the orders whose columns are drawn,
  // and the one the list is in.
  const orderValue = sort === 'stat' && applied.stat !== null ? `${STAT}${applied.stat}` : sort;
  const orderOptions = [
    { value: 'average', label: 'Points a game' },
    { value: 'points', label: pointsWords(range, real) },
    ...(standard && !(real && range === 'recent')
      ? [{ value: 'recent', label: 'Last three games' }]
      : []),
    ...(tools.projections ? [{ value: 'projection', label: 'This week’s projection' }] : []),
    ...((pro && hasDeeper) || sort === 'low' ? [{ value: 'low', label: 'Lowest week' }] : []),
    ...((pro && hasDeeper) || sort === 'range' ? [{ value: 'range', label: 'Highest week' }] : []),
    ...((pro && showOwn) || sort === 'ownership'
      ? [{ value: 'ownership', label: 'Ownership' }]
      : []),
    ...(((pro && showOwn) || sort === 'trend') && tools.suggestions
      ? [{ value: 'trend', label: 'Trending' }]
      : []),
    // By the depth chart where somebody listed is on one (B-479).
    ...(chartKnown || sort === 'depth' ? [{ value: 'depth', label: 'Depth chart' }] : []),
    { value: 'name', label: 'Name' },
    ...statColumns.map((c) => ({ value: `${STAT}${c.key}`, label: c.name.replace(/\.$/, '') })),
  ];
  const searchLine = (
    <Line gap={2}>
      <span className="fsp-players-search">
        <TextField
          label="Search players or teams"
          hideLabel
          placeholder="Search players or teams"
          icon="search"
          value={q}
          onChange={setQ}
        />
      </span>
      <span className="fsp-players-sort">
        <Choice
          label="Order"
          hideLabel
          searchable={false}
          required
          value={orderValue}
          onChange={(v) => {
            if (!v) return;
            setOrder(
              v.startsWith(STAT)
                ? { sort: 'stat', stat: v.slice(STAT.length), reverse: false }
                : { sort: v as PlayerSort, stat: null, reverse: false },
            );
          }}
          options={orderOptions}
        />
      </span>
      {/* PICK PLAYERS TO COMPARE (B-375), from a tablet up beside the
            order; a phone's is under the filters, beside Waivers. */}
      {real && !phone ? pickToggle : null}
    </Line>
  );
  // WHOSE, THEN WHICH POSITIONS, a row each (B-368): one row scrolled
  // sideways, and a phone's positions were all off its edge.
  const whoseChips = (
    <div className="fsp-players-chips fsp-players-whose">
      <Chips
        label="Whose players"
        single
        options={[
          // Free agents and those on waivers: everybody a team may add or claim.
          { value: 'free-agents', label: 'Available' },
          { value: 'waivers', label: 'On waivers' },
          { value: 'taken', label: 'Taken' },
          { value: 'mine', label: 'My team' },
          // The watchlist (E5): a real league's, whoever holds them.
          ...(real ? [{ value: 'watching', label: 'Watching' }] : []),
          { value: 'all', label: 'All' },
        ]}
        value={[availability]}
        onChange={(v) =>
          setAvailability((v[0] as PlayersQuery['availability'] | undefined) ?? 'free-agents')
        }
      />
    </div>
  );
  const positionChips = (
    <div className="fsp-players-chips">
      <Chips
        label="Positions"
        clearable
        options={choices.map((f) => ({ value: f.id, label: f.label, hint: f.name }))}
        value={chosen === null ? [] : [chosen.id]}
        onChange={(v) => setPositions([...(choices.find((f) => f.id === v[0])?.positions ?? [])])}
      />
    </div>
  );
  const clubPicker =
    clubs.length > 0 ? (
      <div className="fsp-players-club">
        <Choice
          label={`${proLeague} team`}
          hideLabel
          placeholder={`All ${proLeague} teams`}
          value={club}
          onChange={(v) => setClub(v || null)}
          options={clubs.map((t) => ({ value: t.id, label: t.name }))}
          clearable
        />
      </div>
    ) : null;
  // ON A PHONE ONLY THE SEARCH AND THE POSITIONS STICK (B-486): every filter
  // stuck took 42% of the screen. Which games and whose players are set once
  // and scroll away above them, and the club scrolls away below them, beside
  // the positions it narrows further. Wider, every filter stays over the
  // list, which has the room.
  const above = phone ? (
    <Stack gap={2}>
      {failureLine}
      {toolsLine}
      {rangeLine}
      {whoseChips}
    </Stack>
  ) : undefined;
  const filters = phone ? (
    <Stack gap={2}>
      {searchLine}
      {positionChips}
    </Stack>
  ) : (
    <Stack gap={2}>
      {failureLine}
      {toolsLine}
      {rangeLine}
      {searchLine}
      {whoseChips}
      {positionChips}
      {clubPicker}
    </Stack>
  );

  // THE PICKING LINE (UX-5): the mode says itself and has its way out, and
  // once two are ticked, Compare where the ticks are (B-407) rather than a
  // pointer to the foot of the screen.
  const pickLine = picking ? (
    <div className="fsp-players-picking-line">
      <span className="fsp-ui-wrap-text" role="status">
        {tray.picked.length === 0
          ? `Tick two players to compare, or up to ${COMPARE_MAX}.`
          : tray.picked.length === 1
            ? 'One picked. Tick one more to compare.'
            : tray.full
              ? `${COMPARE_MAX} picked, the most Compare holds.`
              : `${tray.picked.length} picked, up to ${COMPARE_MAX}.`}
      </span>
      <Button size="xs" variant="quiet" onClick={() => setPicking(false)}>
        Done
      </Button>
      {tray.picked.length >= 2 ? (
        <Button size="xs" variant="primary" icon="compare" onClick={() => tray.open()}>
          {`Compare ${tray.picked.length} players`}
        </Button>
      ) : null}
    </div>
  ) : null;

  // THE WAIVERS PAGE IS A PLACE, NOT A FILTER (B-368): on a phone, which has
  // no rail to reach it from, a button of its own that says where it goes,
  // under the filters rather than among them.
  const toWaivers = phone ? (
    <div className="fsp-players-to-waivers">
      <Button size="xs" variant="secondary" iconRight="next" to={`${base}/waivers`}>
        Waivers and claims
      </Button>
      {real ? pickToggle : null}
    </div>
  ) : null;

  // NO STRIP OF PICKS OVER THE LIST (B-487): Worth a look sat here, and its
  // cards were mostly the list's own top rows, drawn twice.
  const strip =
    toWaivers !== null || pickLine !== null ? (
      <Stack gap={2}>
        {phone ? clubPicker : null}
        {toWaivers}
        {pickLine}
      </Stack>
    ) : null;

  // THE TABLE (B-518): the name fixed at the left, the action at the right,
  // and the fantasy columns and the position's stats scrolling between them
  // when they are wider than the screen.
  const body =
    page === null ? undefined : (
      <StatsTable<Row>
        label={`${page.total ?? page.items.length} players`}
        rows={page.items.map((r) => ({
          key: r.player.id,
          row: r,
          player: r.player,
          stats: r.stats,
          // THE STAR FROM A TABLET UP: a phone's fixed column has no room
          // for it beside the name, and the card has it.
          marks: phone ? undefined : <WatchStar player={r.player} fallback={r.watched} />,
          sub: subOf(r),
          action: actionOf(r) ?? undefined,
          picked: picking && tray.has(r.player.id),
          onOpen: () => openCard(r),
        }))}
        leads={leads}
        columns={statColumns}
        games={false}
        points={false}
        sort={{
          stat: applied.sort === 'stat' ? applied.stat : null,
          reverse: applied.reverse,
          on: sort === 'stat',
          onSort: (stat) => sortBy('stat', stat),
        }}
        short={phone}
        // NO PILL WHERE EVERY ROW IS ONE POSITION (B-520): the chip says it.
        pill={several}
        actionAtEnd
        actionHead={picking ? 'Compare' : undefined}
      />
    );

  return (
    <>
      {/* A refresh that failed over the page on screen says so (B-222). */}
      <StaleNotice snapshot={pool.snapshot} />
      <ListScreen
        above={above}
        filters={filters}
        strip={strip}
        body={body}
        rows={items}
        getKey={(r) => r.player.id}
        renderRow={() => null}
        empty={
          page !== null ? (
            availability === 'watching' && positions.length === 0 && !q && !club ? (
              // The pool is this league's: a star at a position it cannot
              // hold, or from another world, is on the account and not here.
              watchlist.players !== null && watchlist.players.length > 0 ? (
                'None of your starred players are in this league.'
              ) : (
                'Nobody on your watchlist yet. Tap the star by a name to keep an eye on him.'
              )
            ) : (
              'Nobody matches. Try fewer filters.'
            )
          ) : failure !== null ? (
            // The server's own sentence: a refusal names what to fix.
            <Stack gap={1} className="fsp-players-centered">
              <div>{failure.message}</div>
              <Button size="xs" onClick={() => window.location.reload()}>
                Try again
              </Button>
            </Stack>
          ) : (
            'Loading…'
          )
        }
        footer={
          page === null ? null : (
            <>
              {pool.more !== null || pool.loadingMore || pool.moreFailure !== null ? (
                <div className="fsp-players-more">
                  {/* THE NEXT PAGE AS THE LIST NEARS ITS END (B-369); the button
                  stays for a keyboard, and for a page that failed. */}
                  <WhenNear
                    active={pool.more !== null && !pool.loadingMore && pool.moreFailure === null}
                    onNear={() => pool.more?.()}
                  />
                  <span className="fsp-sub">
                    Showing {page.items.length} of {page.total ?? page.items.length}
                  </span>
                  {pool.moreFailure !== null ? (
                    <span className="fsp-sub fsp-ui-wrap-text">{pool.moreFailure}</span>
                  ) : null}
                  <Button
                    size="xs"
                    loading={pool.loadingMore}
                    disabled={pool.more === null && !pool.loadingMore}
                    onClick={() => pool.more?.()}
                  >
                    Show more
                  </Button>
                </div>
              ) : null}
              {/* Where every number above comes from (B-593): a real league's,
                  not the practice league's, whose players are invented. */}
              {real ? <DataCredit /> : null}
            </>
          )
        }
      />
      <PlayerCard data={card} onClose={() => setCard(null)} />
      {/* The projection is this screen's to show at every level, so the sheet
          it opens carries it at every level (B-177). */}
      <RosterMoveSheet move={move} showProjection onClose={() => setMove(null)} />
    </>
  );
}

/**
 * Calls `onNear` when it comes within a screen of the view, while
 * `active`. Observed against the box that scrolls it — the screen on a
 * phone, the list's own region wider up — since that box clips it, and a
 * margin on the viewport would never be reached. Observed again whenever it
 * turns active, so a page that left it in view asks for the next.
 */
function WhenNear({ active, onNear }: { active: boolean; onNear: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const latest = useRef(onNear);
  latest.current = onNear;
  useEffect(() => {
    const el = ref.current;
    if (!active || el === null || typeof IntersectionObserver === 'undefined') return;
    const seen = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) latest.current();
      },
      { root: scrollerOf(el), rootMargin: '800px 0px' },
    );
    seen.observe(el);
    return () => seen.disconnect();
  }, [active]);
  return <div ref={ref} aria-hidden="true" />;
}

/** The nearest ancestor that scrolls vertically, or null for the page itself. */
function scrollerOf(el: HTMLElement): HTMLElement | null {
  for (let a = el.parentElement; a !== null; a = a.parentElement) {
    const y = getComputedStyle(a).overflowY;
    if (y === 'auto' || y === 'scroll') return a;
  }
  return null;
}

function actionLabel(r: PlayerRowView): string {
  if (r.availability.kind === 'waivers' && r.availability.myClaimId) return 'Claimed';
  return { add: 'Add', claim: 'Claim', drop: 'Drop', trade: 'Trade for', none: '' }[r.action];
}
